// Google Identity Services (the standard "Sign in with Google" button).
// The script is loaded from public/index.html; these helpers wait for it and
// build the nonce that ties a Google ID token to this one sign-in attempt.

export const loadGoogleIdentity = () =>
    new Promise((resolve, reject) => {
        if (window.google?.accounts?.id) {
            resolve(window.google.accounts.id);
            return;
        }
        const startedAt = Date.now();
        const poll = setInterval(() => {
            if (window.google?.accounts?.id) {
                clearInterval(poll);
                resolve(window.google.accounts.id);
            } else if (Date.now() - startedAt > 10000) {
                clearInterval(poll);
                reject(new Error('Google sign-in did not load. Check your connection and try again.'));
            }
        }, 50);
    });

const toHex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

// Raw nonce goes to our API; its SHA-256 goes to Google and comes back inside the
// ID token, so the API can prove the token was minted for this attempt.
export const createNonce = async () => {
    const raw = toHex(window.crypto.getRandomValues(new Uint8Array(32)));
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
    return { raw, hashed: toHex(new Uint8Array(digest)) };
};

export const googleClientId = () => process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Trades a Google ID token (plus the raw nonce it was minted for) for our own
// access token. Shared by the sign-in dialog and the One Tap prompt. Resolves
// to the token; rejects with an Error whose message is safe to show the user.
export const exchangeGoogleCredential = async (credential, rawNonce) => {
    let response;
    try {
        response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/google`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credential, nonce: rawNonce }),
        });
    } catch {
        throw new Error('Network error. Check your connection and try again.');
    }

    if (response.ok) return response.text();

    let message = 'Sign-in failed. Please try again.';
    try {
        const body = await response.json();
        if (body?.message) message = body.message;
    } catch { /* keep default */ }
    throw new Error(message);
};
